import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BoundedApiError, type AuditDetail } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useNames } from '../../api/names';
import { ConfirmationChip, ErrorNotice, Provenance, Verdict } from '@bounded/ui/state';
import { Button, Empty, Facts, Panel, SectionHeader, SettingRow, Skeleton, Table } from '@bounded/ui/primitives';
import { GraphView, PathList, ResultLabel } from '../../components/graph/graph-view';
import { configSummary, type AnyGraph, type TraceStep } from '../../lib/graph';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/projects/$projectId/decisions/$deviceId/$evaluationId')({ component: DecisionDetail });

type Evidence = AuditDetail['context']['models'][string][string];
function evidenceValue(e: Evidence) {
  const v = e.value;
  if (!v) return '—';
  if (v.kind === 'predicate') return String(v.value);
  if (v.kind === 'noul') return v.value.toFixed(2);
  if (v.kind === 'choice') return `${v.choice} (${v.confidence.toFixed(2)})`;
  return `${v.score} (${v.confidence.toFixed(2)})`;
}

function DecisionDetail() {
  const t = useT(); const m = t.audit; const f = useFormat();
  const { projectId, deviceId, evaluationId } = Route.useParams();
  const names = useNames();
  const detail = useQuery(q.decision(deviceId, projectId, evaluationId));
  const [selected, setSelected] = useState<string | null>(null);
  const back = <Link to="/projects/$projectId/decisions" params={{ projectId }} className="text-sm text-graphite hover:text-ink">{m.back}</Link>;

  if (detail.error instanceof BoundedApiError && detail.error.status === 404) {
    return <div className="grid gap-4">{back}<Panel><Empty title={m.noDetail}>{m.noDetailBody}</Empty></Panel></div>;
  }
  if (detail.error) return <div className="grid gap-4">{back}<ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} /></div>;
  if (!detail.data) return <div className="grid gap-4">{back}<Skeleton className="h-12 w-72" /><Skeleton className="h-96" /></div>;

  const { detail: d, received_at } = detail.data;
  const graph = d.context.graph as AnyGraph;
  const trace = d.trace as TraceStep[];
  const selectedStep = selected ? trace.find((s) => s.node_id === selected) : undefined;
  const evidence = Object.entries(d.context.models).flatMap(([node, answers]) => Object.entries(answers).map(([question, e]) => ({ node, question, e })));

  return (
    <div className="grid gap-6">
      {back}
      <SectionHeader
        title={<span className="inline-flex items-center gap-3"><Verdict outcome={d.decision.outcome} className="text-xl" /></span>}
        description={d.decision.reason_codes.map((r) => t.decisions.reasons[r] ?? r).join('; ')} />
      <Panel footer={<Provenance>{m.provenance}</Provenance>}>
        <div className="p-4">
          <Facts items={[
            [m.device, <Link to="/projects/$projectId/devices/$deviceId" params={{ projectId, deviceId }} className="hover:underline">{names.device(deviceId)}</Link>],
            [m.agent, `${d.action.agent.name} ${d.action.agent.version}`],
            [m.task, <code>{d.action.scope.task_id}</code>],
            [m.when, `${m.decidedAt(f.dateTime(d.decision.decided_at))}; ${m.received(f.ago(received_at))}`],
          ]} />
          {d.context.locked && <p className="mt-3 text-deny">{m.locked}</p>}
        </div>
      </Panel>

      {/* Device-written content: shown as data, never interpreted. */}
      <Panel title={m.action} description={m.normalization[d.action.normalization] ?? d.action.normalization}>
        <div className="grid gap-4 p-4">
          <ul className="grid gap-1.5">
            {d.action.operations.map((op, i) => (
              <li key={i} className="grid gap-x-3 sm:grid-cols-[9rem_minmax(0,1fr)]">
                <span className="text-graphite">{t.approval.kinds[op.kind] ?? op.kind}</span>
                <span className="min-w-0"><code className="[overflow-wrap:anywhere]">{op.operation}</code> <span className="font-mono text-sm [overflow-wrap:anywhere]">{op.target.locator}</span>
                  {op.target.environment && <span className="ml-2 rounded bg-ask/10 px-1.5 text-sm text-ask">{op.target.environment}</span>}</span>
              </li>
            ))}
          </ul>
          <div>
            <p className="text-sm text-graphite">{t.approval.arguments(<code>{d.action.raw_tool.name}</code>)}</p>
            <pre className="mt-1.5 max-h-72 overflow-auto rounded-[var(--radius-control)] bg-sunken p-3 font-mono text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">{JSON.stringify(d.action.raw_tool.arguments, null, 2)}</pre>
          </div>
        </div>
      </Panel>

      <Panel title={t.graph.pathTitle}>
        <div className="hidden gap-4 p-4 md:grid">
          <GraphView graph={graph} trace={trace} selected={selected} onSelect={setSelected} height={440} />
          <NodeInspector graph={graph} id={selected} step={selectedStep} />
        </div>
        {/* Narrow screens (and screen readers) get the same path as an ordered list. */}
        <div className="md:hidden"><PathList graph={graph} trace={trace} /></div>
        <details className="hidden border-t border-rule md:block">
          <summary className="cursor-pointer px-4 py-3 text-sm text-graphite hover:text-ink">{t.graph.stepsTitle}</summary>
          <PathList graph={graph} trace={trace} />
        </details>
      </Panel>

      <Panel title={m.evidenceTitle}>
        {evidence.length === 0 ? <p className="px-4 py-4 text-graphite">{m.noEvidence}</p> : (
          <Table head={[{ label: t.graph.ops['model.classify'] }, { label: m.question }, { label: m.answer }, { label: m.value }, { label: m.time, className: 'text-right' }]}>
            {evidence.map(({ node, question, e }) => (
              <tr key={`${node}/${question}`}>
                <td><code>{node}</code><span className="block text-sm text-graphite">{e.provider.resolved_model ?? e.provider.requested_model ?? e.provider.kind}</span></td>
                <td><code>{question}</code></td>
                <td>{m.evidenceStatus[e.status] ?? e.status}</td>
                <td className="font-mono text-sm">{evidenceValue(e)}</td>
                <td className="text-right text-sm text-graphite tabular-nums">{m.latency(e.latency_ms)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Replay deviceId={deviceId} projectId={projectId} evaluationId={evaluationId} />
      <DeleteRecord deviceId={deviceId} projectId={projectId} evaluationId={evaluationId} />
    </div>
  );
}

/** Configuration and result of the selected node, as data. */
function NodeInspector({ graph, id, step }: { graph: AnyGraph; id: string | null; step?: TraceStep }) {
  const t = useT();
  const node = id ? graph.nodes[id] : undefined;
  if (!node || !id) return <p className="text-sm text-graphite">{t.graph.selectHint}</p>;
  return (
    <div className="grid content-start gap-4 text-sm md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)]">
      <div><p className="text-graphite">{t.graph.ops[node.op] ?? node.op}</p><p className="font-mono font-medium [overflow-wrap:anywhere]">{id}</p>
        {configSummary(node) && <p className="font-mono text-xs text-graphite [overflow-wrap:anywhere]">{configSummary(node)}</p>}</div>
      <div><p className="text-graphite">{t.graph.result}</p><p>{step ? <ResultLabel value={step.result} t={t} /> : t.graph.notReached}</p></div>
      <div><p className="text-graphite">{t.graph.config}</p>
        <pre className="mt-1 max-h-64 overflow-auto rounded bg-sunken p-2 font-mono text-xs whitespace-pre-wrap [overflow-wrap:anywhere]">{JSON.stringify(node.config, null, 2)}</pre></div>
    </div>
  );
}

function Replay({ deviceId, projectId, evaluationId }: { deviceId: string; projectId: string; evaluationId: string }) {
  const t = useT(); const m = t.audit;
  const replay = useMutation({ mutationFn: () => api.replay(deviceId, projectId, evaluationId) });
  const r = replay.data;
  return (
    <Panel>
      <SettingRow label={m.replayTitle} description={m.replayBody}
        control={<Button className="w-full" disabled={replay.isPending} onClick={() => replay.mutate()}>{replay.isPending ? m.replaying : m.replay}</Button>} />
      {(r || replay.error) && (
        <div className="grid gap-2 border-t border-rule px-4 py-4">
          {replay.error && <ErrorNotice error={replay.error} onRetry={() => replay.mutate()} />}
          {r && <>
            <div className="flex flex-wrap items-center gap-3">
              <ConfirmationChip state={r.matches ? 'confirmed' : 'rejected'}>{r.matches ? m.replayMatch : m.replayMismatch}</ConfirmationChip>
            </div>
            <p className="text-sm">{m.replayOutcome(<Verdict outcome={r.original.outcome} />, <Verdict outcome={r.replay.outcome} />)}</p>
            <Provenance>{m.replayCaveat}</Provenance>
          </>}
        </div>
      )}
    </Panel>
  );
}

function DeleteRecord({ deviceId, projectId, evaluationId }: { deviceId: string; projectId: string; evaluationId: string }) {
  const t = useT(); const m = t.audit;
  const queryClient = useQueryClient();
  const controls = useQuery(q.controls(deviceId, projectId));
  const del = useMutation({
    mutationFn: () => api.deleteDetail(deviceId, projectId, evaluationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project', projectId, 'decisions'] }),
  });
  const fullOn = controls.data?.desired.audit_mode === 'full';
  return (
    <Panel className="border-deny/30">
      <SettingRow label={m.deleteTitle}
        description={<>{m.deleteBody}{fullOn && <> <Link to="/projects/$projectId/devices/$deviceId" params={{ projectId, deviceId }} className="text-boundary hover:underline">{m.deleteNeedsOff}</Link></>}</>}
        control={<Button className="w-full" variant="danger" disabled={fullOn || del.isPending || del.isSuccess} onClick={() => del.mutate()}>{m.delete}</Button>} />
      {(del.error || del.isSuccess) && (
        <div className="border-t border-rule px-4 py-4">{del.error ? <ErrorNotice error={del.error} /> : <p>{m.deleted}</p>}</div>
      )}
    </Panel>
  );
}
